import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import { getCurrentRole } from "../auth";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const initialCategoryForm = {
  name: "",
  description: "",
  imageUrl: "",
  source: "local",
};

const initialEditForm = {
  name: "",
  description: "",
  imageUrl: "",
  source: "local",
  active: true,
};

const initialRequirementForm = {
  key: "",
  label: "",
  fieldType: "text",
  required: false,
  optionsText: "",
  sortOrder: 0,
  active: true,
  odooTarget: "note",
  odooFieldName: "",
  odooLabel: "",
};

export default function CategoriesPage() {
  const currentRole = getCurrentRole();
  const canManageRequirements = currentRole === "admin";
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [quickAction, setQuickAction] = useState("default");
  const [createOpen, setCreateOpen] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [requirementForm, setRequirementForm] = useState(initialRequirementForm);
  const [savingRequirementId, setSavingRequirementId] = useState(null);

  const loadCategories = async () => {
    const response = await api.get("/api/categories");
    setCategories(response.data);
  };

  const loadCategoryDetail = async (id) => {
    const [response, requirementsResponse] = await Promise.all([
      api.get(`/api/categories/${id}`),
      api.get(`/api/categories/${id}/requirements`),
    ]);
    setSelectedCategory(response.data);
    setRequirements(requirementsResponse.data || []);
    setEditForm({
      name: response.data.name || "",
      description: response.data.description || "",
      imageUrl: response.data.image_url || "",
      source: response.data.source || "local",
      active: Boolean(response.data.active),
    });
  };

  useEffect(() => {
    loadCategories().catch(() => {
      setMessage({ type: "error", text: "Les categories n'ont pas pu etre chargees." });
    });
  }, []);

  useEffect(() => {
    if (!selectedCategoryId) {
      setSelectedCategory(null);
      setEditForm(initialEditForm);
      setRequirements([]);
      setRequirementForm(initialRequirementForm);
      return;
    }

    loadCategoryDetail(selectedCategoryId).catch(() => {
      setMessage({ type: "error", text: "Les details de la categorie n'ont pas pu etre charges." });
    });
  }, [selectedCategoryId]);

  const filteredCategories = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? categories
      : categories.filter((category) =>
          [category.name, category.description, category.source]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "local") {
      return base.filter((category) => category.source === "local");
    }

    if (quickAction === "odoo") {
      return base.filter((category) => category.source === "odoo");
    }

    if (quickAction === "with-image") {
      return base.filter((category) => category.image_url);
    }

    return base;
  }, [categories, quickAction, search]);

  const handleCreateCategory = async () => {
    try {
      await api.post("/api/categories", categoryForm);
      setCategoryForm(initialCategoryForm);
      setCreateOpen(false);
      await loadCategories();
      setMessage({ type: "success", text: "La categorie a ete creee." });
    } catch (_error) {
      setMessage({ type: "error", text: "La categorie n'a pas pu etre enregistree." });
    }
  };

  const parseRequirementOptions = (optionsText) =>
    String(optionsText || "")
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);

  const handleCreateRequirement = async () => {
    if (!selectedCategoryId) {
      return;
    }

    try {
      await api.post(`/api/categories/${selectedCategoryId}/requirements`, {
        key: requirementForm.key,
        label: requirementForm.label,
        fieldType: requirementForm.fieldType,
        required: requirementForm.required,
        options: parseRequirementOptions(requirementForm.optionsText),
        sortOrder: Number(requirementForm.sortOrder || 0),
        active: requirementForm.active,
        odooTarget: requirementForm.odooTarget,
        odooFieldName: requirementForm.odooFieldName,
        odooLabel: requirementForm.odooLabel,
      });
      setRequirementForm(initialRequirementForm);
      await loadCategoryDetail(selectedCategoryId);
      setMessage({ type: "success", text: "Le champ categorie a ete cree." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le champ categorie n'a pas pu etre enregistre." });
    }
  };

  const handleSaveRequirement = async (requirement) => {
    if (!selectedCategoryId) {
      return;
    }

    try {
      setSavingRequirementId(requirement.id);
      await api.patch(`/api/categories/${selectedCategoryId}/requirements/${requirement.id}`, {
        key: requirement.key,
        label: requirement.label,
        fieldType: requirement.fieldType,
        required: requirement.required,
        options: parseRequirementOptions(requirement.optionsText),
        sortOrder: Number(requirement.sortOrder || 0),
        active: requirement.active,
        odooTarget: requirement.odooTarget || "note",
        odooFieldName: requirement.odooFieldName || "",
        odooLabel: requirement.odooLabel || "",
      });
      await loadCategoryDetail(selectedCategoryId);
      setMessage({ type: "success", text: "Le champ categorie a ete mis a jour." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le champ categorie n'a pas pu etre enregistre." });
    } finally {
      setSavingRequirementId(null);
    }
  };

  const handleDeleteRequirement = async (requirementId) => {
    if (!selectedCategoryId) {
      return;
    }

    try {
      await api.delete(`/api/categories/${selectedCategoryId}/requirements/${requirementId}`);
      await loadCategoryDetail(selectedCategoryId);
      setMessage({ type: "success", text: "Le champ categorie a ete supprime." });
    } catch (_error) {
      setMessage({ type: "error", text: "Le champ categorie n'a pas pu etre supprime." });
    }
  };

  const handleSaveCategory = async () => {
    if (!selectedCategoryId) {
      return;
    }

    try {
      const response = await api.patch(`/api/categories/${selectedCategoryId}`, editForm);
      setSelectedCategory(response.data);
      await loadCategories();
      await loadCategoryDetail(selectedCategoryId);
      setMessage({ type: "success", text: "La categorie a ete mise a jour." });
    } catch (_error) {
      setMessage({ type: "error", text: "La categorie n'a pas pu etre enregistree." });
    }
  };

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Catalogue des categories"
      description="Vue des categories, des visuels et de la structure du catalogue."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={() => setCreateOpen(true)} sx={saasHeroActionButtonSx}>
          + Nouvelle categorie
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Show {filteredCategories.length} entries
        </Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher des categories"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              ...saasInputSx,
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                minHeight: 44,
                backgroundColor: "#fff",
                boxShadow: "none",
              },
            }}
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} />,
            }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select value={quickAction} onChange={(event) => setQuickAction(event.target.value)} displayEmpty sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}>
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="local">Categories locales</MenuItem>
              <MenuItem value="odoo">Aus Odoo</MenuItem>
              <MenuItem value="with-image">Mit Bild</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 106, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>
              {filteredCategories.length} categories
            </Typography>
          </Box>

          <Box sx={{ width: 58, height: 44, borderRadius: "16px", border: "1px solid #dfe6f4", bgcolor: "#fff", display: "grid", placeItems: "center" }}>
            <Badge badgeContent={categories.filter((category) => category.image_url).length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}>
                <NotificationsRoundedIcon />
              </IconButton>
            </Badge>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.1fr 0.9fr" }, gap: 3 }}>
        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Categories</Typography>
              <Typography variant="body2" color="text.secondary">
                Structured view with category image, source and product count.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Visibles</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{filteredCategories.length}</Typography></Box></Box>
              <Box sx={saasCircleCountSx}><Box><Typography variant="caption" sx={{ color: "#7d89a2" }}>Selection</Typography><Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>{selectedCategoryId ? 1 : 0}</Typography></Box></Box>
            </Stack>
          </Box>

          {filteredCategories.map((category) => (
            <DirectoryRowCard
              key={category.id}
              selected={selectedCategoryId === category.id}
              avatar={
                category.image_url ? (
                  <Box component="img" src={category.image_url} alt={category.name} sx={{ width: 42, height: 42, borderRadius: "14px", objectFit: "cover", border: "1px solid #dbe3f3" }} />
                ) : null
              }
              avatarText={(category.name || "C").slice(0, 1).toUpperCase()}
              title={category.name}
              subtitle={`CAT-${category.id}`}
              chips={[
                { label: category.source || "local", color: "default" },
                { label: category.image_url ? "Image" : "Sans image", color: category.image_url ? "success" : "default" },
              ]}
              columns={[
                { label: "Description", value: category.description || "-", secondary: category.image_url ? "Image liee" : "Aucune image liee" },
                { label: "Produits", value: String(category.product_count || 0), secondary: category.active === 0 ? "Inactif" : "Actif" },
              ]}
              actionLabel={selectedCategoryId === category.id ? "Selectionne" : "Choisir"}
              onSelect={() => setSelectedCategoryId(category.id)}
            />
          ))}
        </Box>

        <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 }, alignContent: "start" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>Espace categorie</Typography>
            <Typography variant="body2" color="text.secondary">Gestion du nom, de l'image, de la source et du statut actif.</Typography>
          </Box>

          {!selectedCategory ? (
            <Typography variant="body2" color="text.secondary">Choisissez une categorie a gauche pour la modifier.</Typography>
          ) : (
            <>
              {editForm.imageUrl ? (
                <Box component="img" src={editForm.imageUrl} alt={editForm.name} sx={{ width: "100%", maxHeight: 220, borderRadius: "24px", objectFit: "cover", border: "1px solid #dbe3f3" }} />
              ) : (
                <Box sx={{ height: 180, borderRadius: "24px", border: "1px dashed #cfd8f6", bgcolor: "#f8faff", display: "grid", placeItems: "center", color: "#6d7aa0" }}>
                  <CategoryRoundedIcon />
                </Box>
              )}
              <TextField label="Name" value={editForm.name} onChange={(event) => setEditForm((current) => ({ ...current, name: event.target.value }))} sx={saasInputSx} />
              <TextField label="Beschreibung" multiline minRows={3} value={editForm.description} onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))} sx={saasInputSx} />
              <TextField label="Bild URL" value={editForm.imageUrl} onChange={(event) => setEditForm((current) => ({ ...current, imageUrl: event.target.value }))} sx={saasInputSx} />
              <TextField select label="Quelle" value={editForm.source} onChange={(event) => setEditForm((current) => ({ ...current, source: event.target.value }))} sx={saasInputSx}>
                <MenuItem value="local">locale</MenuItem>
                <MenuItem value="odoo">odoo</MenuItem>
              </TextField>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>Aktiv</Typography>
                <Switch checked={editForm.active} onChange={(event) => setEditForm((current) => ({ ...current, active: event.target.checked }))} />
              </Stack>
              <Button variant="contained" onClick={handleSaveCategory} sx={{ borderRadius: "14px" }}>Enregistrer la categorie</Button>

              <Box sx={{ borderTop: "1px solid #e4eaf5", pt: 2, display: "grid", gap: 1.5 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: 17, mb: 0.3 }}>Champs obligatoires du commercial</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ces champs sont remplis plus tard par le commercial avant la conversion en commande.
                  </Typography>
                </Box>

                {requirements.map((requirement, index) => (
                  <Box key={requirement.id} sx={{ border: "1px solid #e1e7f2", borderRadius: "18px", p: 1.4, display: "grid", gap: 1 }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <TextField
                        label="Cle"
                        value={requirement.key}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, key: event.target.value } : entry
                            )
                          )
                        }
                        sx={saasInputSx}
                        disabled={!canManageRequirements}
                      />
                      <TextField
                        label="Libelle"
                        value={requirement.label}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, label: event.target.value } : entry
                            )
                          )
                        }
                        sx={saasInputSx}
                        disabled={!canManageRequirements}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <TextField
                        select
                        label="Type"
                        value={requirement.fieldType}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, fieldType: event.target.value } : entry
                            )
                          )
                        }
                        sx={saasInputSx}
                        disabled={!canManageRequirements}
                      >
                        <MenuItem value="text">Texte</MenuItem>
                        <MenuItem value="number">Nombre</MenuItem>
                        <MenuItem value="select">Liste</MenuItem>
                        <MenuItem value="boolean">Oui / Non</MenuItem>
                      </TextField>
                      <TextField
                        label="Ordre"
                        type="number"
                        value={requirement.sortOrder}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, sortOrder: event.target.value } : entry
                            )
                          )
                        }
                        sx={saasInputSx}
                        disabled={!canManageRequirements}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <TextField
                        select
                        label="Cible Odoo"
                        value={requirement.odooTarget || "note"}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, odooTarget: event.target.value } : entry
                            )
                          )
                        }
                        sx={saasInputSx}
                        disabled={!canManageRequirements}
                      >
                        <MenuItem value="note">Note commande</MenuItem>
                        <MenuItem value="x_studio">Champ custom Odoo</MenuItem>
                        <MenuItem value="sale_order_line">Ligne de commande</MenuItem>
                        <MenuItem value="product_config">Configuration produit</MenuItem>
                      </TextField>
                      <TextField
                        label="Nom technique Odoo"
                        value={requirement.odooFieldName || ""}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, odooFieldName: event.target.value } : entry
                            )
                          )
                        }
                        sx={saasInputSx}
                        disabled={!canManageRequirements}
                      />
                    </Stack>
                    <TextField
                      label="Libelle export Odoo"
                      value={requirement.odooLabel || ""}
                      onChange={(event) =>
                        setRequirements((current) =>
                          current.map((entry, entryIndex) =>
                            entryIndex === index ? { ...entry, odooLabel: event.target.value } : entry
                          )
                        )
                      }
                      sx={saasInputSx}
                      disabled={!canManageRequirements}
                    />
                    {requirement.fieldType === "select" ? (
                      <TextField
                        label="Options (separees par des virgules)"
                        value={requirement.optionsText ?? (requirement.options || []).join(", ")}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, optionsText: event.target.value } : entry
                            )
                          )
                        }
                        sx={saasInputSx}
                        disabled={!canManageRequirements}
                      />
                    ) : null}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>Obligatoire</Typography>
                      <Switch
                        checked={Boolean(requirement.required)}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, required: event.target.checked } : entry
                            )
                          )
                        }
                        disabled={!canManageRequirements}
                      />
                      <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>Actif</Typography>
                      <Switch
                        checked={Boolean(requirement.active)}
                        onChange={(event) =>
                          setRequirements((current) =>
                            current.map((entry, entryIndex) =>
                              entryIndex === index ? { ...entry, active: event.target.checked } : entry
                            )
                          )
                        }
                        disabled={!canManageRequirements}
                      />
                    </Stack>
                    {canManageRequirements ? (
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                        <Button
                          variant="contained"
                          onClick={() => handleSaveRequirement(requirement)}
                          disabled={savingRequirementId === requirement.id}
                          sx={{ borderRadius: "14px" }}
                        >
                          Enregistrer le champ
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteRequirement(requirement.id)}
                          sx={{ borderRadius: "14px" }}
                        >
                          Supprimer
                        </Button>
                      </Stack>
                    ) : null}
                  </Box>
                ))}

                {canManageRequirements ? (
                  <Box sx={{ border: "1px dashed #ccd7ea", borderRadius: "18px", p: 1.4, display: "grid", gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "#25324b", fontWeight: 800 }}>Nouveau champ</Typography>
                    <TextField label="Cle" value={requirementForm.key} onChange={(event) => setRequirementForm((current) => ({ ...current, key: event.target.value }))} sx={saasInputSx} />
                    <TextField label="Libelle" value={requirementForm.label} onChange={(event) => setRequirementForm((current) => ({ ...current, label: event.target.value }))} sx={saasInputSx} />
                    <TextField select label="Type" value={requirementForm.fieldType} onChange={(event) => setRequirementForm((current) => ({ ...current, fieldType: event.target.value }))} sx={saasInputSx}>
                      <MenuItem value="text">Texte</MenuItem>
                      <MenuItem value="number">Nombre</MenuItem>
                      <MenuItem value="select">Liste</MenuItem>
                      <MenuItem value="boolean">Oui / Non</MenuItem>
                    </TextField>
                    {requirementForm.fieldType === "select" ? (
                      <TextField label="Options (separees par des virgules)" value={requirementForm.optionsText} onChange={(event) => setRequirementForm((current) => ({ ...current, optionsText: event.target.value }))} sx={saasInputSx} />
                    ) : null}
                    <TextField label="Ordre" type="number" value={requirementForm.sortOrder} onChange={(event) => setRequirementForm((current) => ({ ...current, sortOrder: event.target.value }))} sx={saasInputSx} />
                    <TextField select label="Cible Odoo" value={requirementForm.odooTarget} onChange={(event) => setRequirementForm((current) => ({ ...current, odooTarget: event.target.value }))} sx={saasInputSx}>
                      <MenuItem value="note">Note commande</MenuItem>
                      <MenuItem value="x_studio">Champ custom Odoo</MenuItem>
                      <MenuItem value="sale_order_line">Ligne de commande</MenuItem>
                      <MenuItem value="product_config">Configuration produit</MenuItem>
                    </TextField>
                    <TextField label="Nom technique Odoo" value={requirementForm.odooFieldName} onChange={(event) => setRequirementForm((current) => ({ ...current, odooFieldName: event.target.value }))} sx={saasInputSx} />
                    <TextField label="Libelle export Odoo" value={requirementForm.odooLabel} onChange={(event) => setRequirementForm((current) => ({ ...current, odooLabel: event.target.value }))} sx={saasInputSx} />
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ color: "#25324b", fontWeight: 700 }}>Obligatoire</Typography>
                      <Switch checked={requirementForm.required} onChange={(event) => setRequirementForm((current) => ({ ...current, required: event.target.checked }))} />
                    </Stack>
                    <Button variant="contained" onClick={handleCreateRequirement} sx={{ borderRadius: "14px" }}>
                      Ajouter le champ
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Seul un administrateur peut modifier ces champs.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Creer une categorie</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1.5 }}>
          <TextField label="Nom" value={categoryForm.name} onChange={(event) => setCategoryForm((current) => ({ ...current, name: event.target.value }))} sx={saasInputSx} />
          <TextField label="Description" multiline minRows={3} value={categoryForm.description} onChange={(event) => setCategoryForm((current) => ({ ...current, description: event.target.value }))} sx={saasInputSx} />
          <TextField label="URL image" value={categoryForm.imageUrl} onChange={(event) => setCategoryForm((current) => ({ ...current, imageUrl: event.target.value }))} sx={saasInputSx} />
          <TextField select label="Source" value={categoryForm.source} onChange={(event) => setCategoryForm((current) => ({ ...current, source: event.target.value }))} sx={saasInputSx}>
            <MenuItem value="local">locale</MenuItem>
            <MenuItem value="odoo">odoo</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreateCategory} disabled={!categoryForm.name}>Enregistrer la categorie</Button>
        </DialogActions>
      </Dialog>
    </PageFrame>
  );
}
